import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BlogCard } from './BlogCard';
import styles from './BlogGrid.module.css';

interface Article {
  slug: string;
  title: string;
  description: string;
  date: string;
  author: string;
  category: string;
  readingTime: number;
  thumbnail: string;
}

interface BlogGridProps {
  articles: Article[];
}

const ARTICLES_PER_PAGE = 6;

export function BlogGrid({ articles }: BlogGridProps) {
  const { t } = useTranslation('blog');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const categories = useMemo(() => {
    const cats = new Set(articles.map((a) => a.category));
    return Array.from(cats);
  }, [articles]);

  const filtered = useMemo(() => {
    if (!activeCategory) return articles;
    return articles.filter((a) => a.category === activeCategory);
  }, [articles, activeCategory]);

  const totalPages = Math.ceil(filtered.length / ARTICLES_PER_PAGE);
  const paginated = filtered.slice(
    (currentPage - 1) * ARTICLES_PER_PAGE,
    currentPage * ARTICLES_PER_PAGE
  );

  const handleCategoryChange = (cat: string | null) => {
    setActiveCategory(cat);
    setCurrentPage(1);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.filters}>
        <button
          className={`${styles.filterBtn} ${activeCategory === null ? styles.filterActive : ''}`}
          onClick={() => handleCategoryChange(null)}
        >
          {t('listing.all')}
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            className={`${styles.filterBtn} ${activeCategory === cat ? styles.filterActive : ''}`}
            onClick={() => handleCategoryChange(cat)}
          >
            {t(`categories.${cat}`)}
          </button>
        ))}
      </div>

      {paginated.length === 0 ? (
        <p className={styles.empty}>{t('listing.empty')}</p>
      ) : (
        <div className={styles.grid}>
          {paginated.map((article) => (
            <BlogCard key={article.slug} {...article} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            onClick={() => setCurrentPage((p) => p - 1)}
            disabled={currentPage === 1}
          >
            {t('listing.previous')}
          </button>
          <span className={styles.pageInfo}>
            {currentPage} / {totalPages}
          </span>
          <button
            className={styles.pageBtn}
            onClick={() => setCurrentPage((p) => p + 1)}
            disabled={currentPage === totalPages}
          >
            {t('listing.next')}
          </button>
        </div>
      )}
    </div>
  );
}
